import React, { useEffect, useState } from "react";
import Axios from "../../_services/caller.service";
import styles from "./ManualReminders.module.scss";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";
import { FiSend, FiRefreshCw, FiCheckCircle, FiArrowUp, FiArrowDown } from "react-icons/fi";

// Panneau d'envoi manuel des rappels de calibration (mode manuel).
// Liste les calibrations dans la fenêtre de rappel, permet de cocher
// ligne par ligne (cabine + transducteur) et d'envoyer les rappels choisis.
// L'envoi réel reste soumis aux garde-fous serveur (prod + ENABLE_EMAILS).
const ManualReminders = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState({}); // key -> true
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showSent, setShowSent] = useState(false);
  const [search, setSearch] = useState("");
  const [resendingKey, setResendingKey] = useState(null);
  // true = plus en retard d'abord (jours restants croissants, négatifs = retard).
  const [mostLateFirst, setMostLateFirst] = useState(true);

  // Clé unique par destinataire (cabine × transducteur × utilisateur).
  const recipientKey = (r, rcp) => `${r.cabid}|${r.Eoutpout}|${rcp.user_id}`;

  // Mention de rôle (Admin / Owner / Calibration) à afficher à côté du profil.
  const roleBadge = (role) => {
    switch (Number(role)) {
      case 256:
        return { label: t("reminders.role_admin"), className: styles.roleAdmin };
      case 2147483647:
        return { label: t("reminders.role_owner"), className: styles.roleOwner };
      case 8:
        return {
          label: t("reminders.role_calibration"),
          className: styles.roleCalibration,
        };
      default:
        return null;
    }
  };

  // Un destinataire est sélectionnable s'il accepte les emails ET n'a pas
  // déjà reçu ce rappel.
  const isSelectable = (rcp) => rcp.canSend && !rcp.alreadySent;

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await Axios.get("/getManualReminderCandidates");
      setRows(res.data?.data || []);
      setSelected({});
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  // Toutes les paires (ligne, destinataire) sélectionnables.
  const selectablePairs = rows.flatMap((r) =>
    (r.recipients || [])
      .filter(isSelectable)
      .map((rcp) => ({ r, rcp }))
  );

  // Filtre texte : cabine, transducteur, nom ou email d'un destinataire.
  const matchesSearch = (r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const inRow =
      `${r.cabname || ""} ${r.cabid || ""} ${r.Eoutpout || ""}`
        .toLowerCase()
        .includes(q);
    const inRecipients = (r.recipients || []).some((rcp) =>
      `${rcp.display_name || ""} ${rcp.user_login || ""} ${
        rcp.user_email || ""
      }`
        .toLowerCase()
        .includes(q)
    );
    return inRow || inRecipients;
  };

  // Lignes affichées :
  //  - showSent actif : uniquement les lignes ayant au moins un envoi ;
  //  - sinon : uniquement les lignes ayant au moins un destinataire non envoyé.
  const visibleRows = rows
    .filter(matchesSearch)
    .filter((r) =>
      showSent
        ? (r.recipients || []).some((rcp) => rcp.alreadySent)
        : (r.recipients || []).some((rcp) => !rcp.alreadySent)
    )
    .sort((a, b) => {
      const da = Number(a.numberOfDays ?? 0);
      const db = Number(b.numberOfDays ?? 0);
      // Le plus en retard = le plus petit nombre de jours (le plus négatif).
      return mostLateFirst ? da - db : db - da;
    });

  const sentCount = rows.reduce(
    (acc, r) =>
      acc + (r.recipients || []).filter((rcp) => rcp.alreadySent).length,
    0
  );

  const toggleRecipient = (r, rcp) => {
    if (!isSelectable(rcp)) return;
    const key = recipientKey(r, rcp);
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allSelected =
    selectablePairs.length > 0 &&
    selectablePairs.every(({ r, rcp }) => selected[recipientKey(r, rcp)]);

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      selectablePairs.forEach(({ r, rcp }) => {
        next[recipientKey(r, rcp)] = true;
      });
      setSelected(next);
    }
  };

  const selectedItems = selectablePairs.filter(
    ({ r, rcp }) => selected[recipientKey(r, rcp)]
  );

  const sendSelected = async () => {
    if (selectedItems.length === 0) return;
    setConfirmOpen(false);
    setSending(true);
    try {
      const items = selectedItems.map(({ r, rcp }) => ({
        cabid: r.cabid,
        userId: rcp.user_id,
        Eoutpout: r.Eoutpout,
      }));
      const res = await Axios.post("/sendRemindersManual", { items });
      const { sent, total } = res.data;
      if (sent === total) {
        toast.success(t("reminders.manual_sent", { count: sent, total }));
      } else {
        toast.info(t("reminders.manual_sent", { count: sent, total }));
      }
      // Rafraîchir pour refléter les statuts mis à jour
      fetchCandidates();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setSending(false);
    }
  };

  // Renvoi d'un email déjà envoyé (force le contournement de l'anti-doublon).
  const resendRecipient = async (r, rcp) => {
    const key = recipientKey(r, rcp);
    setResendingKey(key);
    try {
      const res = await Axios.post("/sendRemindersManual", {
        force: true,
        items: [
          { cabid: r.cabid, userId: rcp.user_id, Eoutpout: r.Eoutpout },
        ],
      });
      const { sent } = res.data;
      if (sent > 0) {
        toast.success(t("reminders.resend_success"));
      } else {
        toast.info(t("reminders.manual_sent", { count: 0, total: 1 }));
      }
      fetchCandidates();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setResendingKey(null);
    }
  };

  return (
    <PermissionGate actionKey={"manageCertificat.remindCalibrationEmail"}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <h4>{t("reminders.manual_title")}</h4>
          <div className={styles.headerActions}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder={t("reminders.manual_search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className={styles.showSentToggle}>
              <input
                type="checkbox"
                checked={showSent}
                onChange={() => setShowSent((v) => !v)}
              />
              {t("reminders.manual_show_sent")}
            </label>
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={fetchCandidates}
              disabled={loading}
            >
              <FiRefreshCw /> {t("common.refresh")}
            </button>
            <button
              type="button"
              className={styles.sendBtn}
              onClick={() => setConfirmOpen(true)}
              disabled={sending || selectedItems.length === 0}
            >
              <FiSend /> {t("reminders.manual_send_btn")}
              {selectedItems.length > 0 && (
                <span className={styles.sendCount}>{selectedItems.length}</span>
              )}
            </button>
          </div>
        </div>

        <p className={styles.note}>{t("reminders.manual_note")}</p>
        <p className={styles.scopeInfo}>{t("reminders.manual_scope_info")}</p>

        {confirmOpen && (
          <div className={styles.confirmBar}>
            <div className={styles.confirmBarText}>
              <strong>{t("reminders.manual_confirm_title")}</strong>
              <span>{t("reminders.manual_confirm_text", { count: selectedItems.length })}</span>
            </div>
            <div className={styles.confirmBarActions}>
              <button
                type="button"
                className={styles.modalCancel}
                onClick={() => setConfirmOpen(false)}
                disabled={sending}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className={styles.modalConfirm}
                onClick={sendSelected}
                disabled={sending}
              >
                <FiSend /> {t("reminders.manual_confirm_btn")}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p>{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>{t("reminders.manual_empty")}</p>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            selectedItems.length > 0 && !allSelected;
                      }}
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={selectablePairs.length === 0}
                    />
                  </th>
                  <th>{t("reminders.cabine")}</th>
                  <th>{t("reminders.transducer")}</th>
                  <th>
                    <button
                      type="button"
                      className={styles.sortHeaderBtn}
                      onClick={() => setMostLateFirst((v) => !v)}
                      title={t("reminders.manual_sort_hint")}
                    >
                      {t("calibration_test.days")}{" "}
                      {mostLateFirst ? <FiArrowUp /> : <FiArrowDown />}
                    </button>
                  </th>
                  <th>{t("reminders.recipients")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const recipients = showSent
                    ? (r.recipients || []).filter((rcp) => rcp.alreadySent)
                    : (r.recipients || []).filter((rcp) => !rcp.alreadySent);
                  if (recipients.length === 0) return null;
                  const selectable = recipients.filter(isSelectable);
                  const selectedCount = selectable.filter(
                    (rcp) => selected[recipientKey(r, rcp)]
                  ).length;
                  const allOn =
                    selectable.length > 0 &&
                    selectedCount === selectable.length;
                  const someOn = selectedCount > 0 && !allOn;
                  return (
                    <tr key={`${r.cabid}|${r.Eoutpout}`}>
                      <td>
                        <input
                          type="checkbox"
                          ref={(el) => {
                            if (el) el.indeterminate = someOn;
                          }}
                          checked={allOn}
                          onChange={() => {
                            setSelected((prev) => {
                              const next = { ...prev };
                              selectable.forEach((rcp) => {
                                next[recipientKey(r, rcp)] = !allOn;
                              });
                              return next;
                            });
                          }}
                          disabled={selectable.length === 0}
                        />
                      </td>
                      <td>{r.cabname || r.cabid}</td>
                      <td>{r.Eoutpout}</td>
                      <td>{r.numberOfDays}</td>
                      <td>
                        <ul className={styles.recipientList}>
                          {recipients.map((rcp) => {
                            const key = recipientKey(r, rcp);
                            const selectable = isSelectable(rcp);
                            return (
                              <li
                                key={key}
                                className={`${styles.recipientItem} ${
                                  !rcp.canSend ? styles.recipientOptOut : ""
                                } ${rcp.alreadySent ? styles.recipientSent : ""}`}
                              >
                                <label className={styles.recipientLabel}>
                                  <input
                                    type="checkbox"
                                    checked={!!selected[key]}
                                    onChange={() => toggleRecipient(r, rcp)}
                                    disabled={!selectable}
                                  />
                                  <span className={styles.recipientName}>
                                    {rcp.display_name ||
                                      rcp.user_login ||
                                      rcp.user_email}
                                  </span>
                                  {roleBadge(rcp.role) && (
                                    <span
                                      className={`${styles.roleBadge} ${
                                        roleBadge(rcp.role).className
                                      }`}
                                    >
                                      {roleBadge(rcp.role).label}
                                    </span>
                                  )}
                                  <span className={styles.recipientEmail}>
                                    {rcp.user_email}
                                  </span>
                                </label>
                                {rcp.alreadySent && (
                                  <span className={styles.sentBadge}>
                                    <FiCheckCircle />{" "}
                                    {t("reminders.manual_already_sent")}
                                  </span>
                                )}
                                {rcp.alreadySent && rcp.canSend && (
                                  <button
                                    type="button"
                                    className={styles.resendBtn}
                                    onClick={() => resendRecipient(r, rcp)}
                                    disabled={resendingKey === key}
                                  >
                                    <FiRefreshCw /> {t("reminders.resend")}
                                  </button>
                                )}
                                {!rcp.canSend && (
                                  <span className={styles.optOutBadge}>
                                    {t("reminders.recipient_opt_out")}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {visibleRows.length === 0 && (
              <p className={styles.empty}>
                {showSent
                  ? t("reminders.manual_no_sent")
                  : t("reminders.manual_all_sent", { count: sentCount })}
              </p>
            )}

            <div className={styles.footer}>
              {sentCount > 0 && (
                <span className={styles.count}>
                  {t("reminders.manual_sent_count", { count: sentCount })}
                </span>
              )}
              <span className={styles.count}>
                {t("reminders.manual_selected_count", {
                  count: selectedItems.length,
                })}
              </span>
            </div>
          </>
        )}
      </div>
    </PermissionGate>
  );
};

export default ManualReminders;
